import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import {
  Currency,
  FiatWalletAccountState,
  FiatWalletKycState,
} from '@sorare/core/src/__generated__/globalTypes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useFiatBalance } from '@sorare/core/src/hooks/wallets/useFiatBalance';
import { isType } from '@sorare/core/src/lib/gql';

import type { useFiatWalletValidation_taskInterface } from './__generated__/useFiatWalletValidation.graphql';

type Props = {
  task: useFiatWalletValidation_taskInterface;
};
export const useFiatWalletValidation = ({ task }: Props) => {
  const { currentUser, fiatWalletAccountable } = useCurrentUserContext();
  const { kycStatus } = useFiatBalance();

  const kycUnderReview =
    kycStatus &&
    [
      FiatWalletKycState.VALIDATION_ASKED,
      FiatWalletKycState.CREATED,
    ].includesValue(kycStatus);

  const kycCompleted =
    !kycUnderReview &&
    fiatWalletAccountable?.state === FiatWalletAccountState.VALIDATED_OWNER;

  const userWantsMoneratyRewardInFiat =
    currentUser?.userSettings.rewardCurrency === Currency.FIAT;

  const rewardConfigs = task.rewardConfigs || [];
  const hasCashReward = rewardConfigs.some(reward =>
    isType(reward, 'MonetaryRewardConfig')
  );
  const needsKYCValidation =
    !kycCompleted && hasCashReward && userWantsMoneratyRewardInFiat;

  return { needsKYCValidation, kycUnderReview };
};

useFiatWalletValidation.fragments = {
  taskInterface: gql`
    fragment useFiatWalletValidation_taskInterface on TaskInterface {
      id
      rewardConfigs {
        id
        ... on MonetaryRewardConfig {
          id
        }
      }
    }
  ` as TypedDocumentNode<useFiatWalletValidation_taskInterface>,
};
